import React, {ChangeEvent, FunctionComponent} from "react";
import {Field, FieldProps} from "formik";
import {format} from "date-fns";
import {Manager, Popper, Reference} from "react-popper";
import {css as classCss} from "@emotion/css";
import {
  DATE_FORMAT,
  DateUnit,
  OutsideClickHandler,
  Icon,
  type CheckboxProps,
  type IconProps,
} from "@reside/ui";

import {useTaskCheckboxContext} from "./TaskCheckboxContext";
import {emptyChecklistItemWithStatus} from "./consts";
import {
  CheckedStatusEnum,
  type FieldValue,
  type TaskCheckboxChildren,
  type TaskCheckboxFieldProps,
} from "./types";
import {
  coalesceCheckedStatus,
  deserializeDateValue,
  getDateRangeAfter,
  getDateRangeBefore,
  isFinalStatus,
} from "./helpers";
import {
  FormattedDate,
  StyledDropdownSelectList,
  StyledDropdownSelectOption,
  StyledTooltip,
} from "./styles";
import {
  defaultTaskCheckboxFieldProps,
  taskCheckboxFadedSmallOverrides,
  taskCheckboxSmallOverrides,
} from "./defaults";

export {CheckedStatusEnum};
export type {TaskCheckboxChildren, TaskCheckboxFieldProps, FieldValue};

const WithTooltip: FunctionComponent<{
  disabled: boolean;
  visible: boolean;
  label: string;
  children: React.ReactNode;
}> = ({children, visible, label, disabled}) => (
  <>
    {visible ? (
      <StyledTooltip content={label} disabled={disabled}>
        {children}
      </StyledTooltip>
    ) : (
      children
    )}
  </>
);

/**
 * A formik field for a task checkbox with status and date tracking.
 *
 * @param {TaskCheckboxFieldProps} props The props for the component.
 * @returns {JSX.Element} The rendered task checkbox field component.
 */
export const TaskCheckboxField = (props: TaskCheckboxFieldProps) => {
  const {
    label,
    name,
    disabled,
    onComplete,
    children = defaultTaskCheckboxFieldProps.children,
    CheckboxWrapper = defaultTaskCheckboxFieldProps.CheckboxWrapper,
    Checkbox = defaultTaskCheckboxFieldProps.Checkbox,
    Label = defaultTaskCheckboxFieldProps.Label,
    LabelText = defaultTaskCheckboxFieldProps.LabelText,
    ClickOutsideWrapper = defaultTaskCheckboxFieldProps.ClickOutsideWrapper,
    renderTrailingWithCaret,
    renderNotApplicableStatusOption = defaultTaskCheckboxFieldProps.renderNotApplicableStatusOption,
    tooltipLength = defaultTaskCheckboxFieldProps.tooltipLength,
    DatePicker = defaultTaskCheckboxFieldProps.DatePicker,
    size,
    checkboxBoxPx = 14,
    ...restProps
  } = {...defaultTaskCheckboxFieldProps, ...props};

  const {openedCheckboxElement, setOpenedCheckboxElement, setCheckboxClosed} =
    useTaskCheckboxContext();

  const emptyValue = emptyChecklistItemWithStatus;

  return (
    <Field name={name}>
      {({
        form,
        field: {value: fieldValue = emptyValue, ...field},
      }: FieldProps<FieldValue>) => {
        const currentCheckedStatus = coalesceCheckedStatus(fieldValue);

        const handleStatusChange = (
          checkedStatus: FieldValue["checkedStatus"],
        ) => {
          if (currentCheckedStatus === checkedStatus) {
            setCheckboxClosed();
            return;
          }

          form.setFieldTouched(name, true, true);

          const newValue = {
            ...fieldValue,
            checkedStatus,
            checked: isFinalStatus(checkedStatus),
            checkedDate:
              fieldValue.checkedDate ??
              format(new Date(), DATE_FORMAT.ISO_DATE),
          };
          form.setFieldValue(name, newValue, true);
          setCheckboxClosed();
        };

        const handleDateChange = (date: Date, unit: DateUnit) => {
          if (unit !== "day") {
            return;
          }
          form.setFieldTouched(name, true, true);
          const newValue = {
            ...fieldValue,
            checked: true,
            checkedDate: format(date, DATE_FORMAT.ISO_DATE),
          };
          form.setFieldValue(name, newValue, true);
          setCheckboxClosed();
        };

        const isFinal = isFinalStatus(currentCheckedStatus);
        const isOpened = openedCheckboxElement?.name === name;
        const isStatusDropdownOpen =
          Boolean(isOpened) &&
          openedCheckboxElement?.element === "STATUS_DROPDOWN";
        const isSmall = size === "small";
        const smallInProgressIconSize = checkboxBoxPx === 16 ? 14 : 12;

        return (
          <Manager>
            <Reference>
              {({ref}) => (
                <div ref={ref}>
                  <CheckboxWrapper
                    style={
                      {
                        ["--task-checkbox-box-px"]: `${checkboxBoxPx}px`,
                      } as React.CSSProperties
                    }
                  >
                    <Label
                      disabled={disabled}
                      isOpened={isOpened}
                      data-status-dropdown-open={
                        isStatusDropdownOpen ? "true" : undefined
                      }
                    >
                      {(() => {
                        const taskCheckboxSlots: TaskCheckboxChildren = {
                          hasCheckedStatus: Boolean(fieldValue.checkedStatus),
                          name,
                          Checkbox: (customProps: Partial<CheckboxProps>) => (
                            <>
                              <Checkbox
                                {...field}
                                {...restProps}
                                name={name}
                                disabled={disabled}
                                size={size}
                                color={isFinal ? "mint100" : "primary"}
                                checked={
                                  fieldValue.checked ||
                                  currentCheckedStatus ===
                                    CheckedStatusEnum.IN_PROGRESS
                                }
                                Icon={(iconProps: IconProps) =>
                                  currentCheckedStatus ===
                                  CheckedStatusEnum.IN_PROGRESS ? (
                                    <Icon
                                      {...iconProps}
                                      name="angle-double-right"
                                      size={
                                        isSmall ? smallInProgressIconSize : 20
                                      }
                                      className={
                                        isSmall
                                          ? classCss`margin-left: 1px;`
                                          : classCss`
                                        margin-top: 1px;
                                        margin-left: 2px;
                                      `
                                      }
                                    />
                                  ) : currentCheckedStatus ===
                                    CheckedStatusEnum.NOT_APPLICABLE ? (
                                    <Icon
                                      {...iconProps}
                                      name="block"
                                      size={isSmall ? 12 : 18}
                                      className={classCss`
                                      margin-top: 2px;
                                    `}
                                    />
                                  ) : (
                                    <Icon
                                      {...iconProps}
                                      name="check"
                                      className={
                                        isSmall
                                          ? ""
                                          : classCss`
                                        margin-top: 2px;
                                        margin-left: 1px;
                                      `
                                      }
                                    />
                                  )
                                }
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (event.target.checked) {
                                    setOpenedCheckboxElement({
                                      name,
                                      element: "STATUS_DROPDOWN",
                                    });
                                  } else {
                                    form.setFieldTouched(name, true, true);
                                    form.setFieldValue(name, emptyValue, true);
                                  }
                                }}
                                {...customProps}
                              />

                              {currentCheckedStatus === CheckedStatusEnum.OPEN ||
                              renderTrailingWithCaret ? (
                                <span data-task-checkbox-trailing="">
                                  {currentCheckedStatus ===
                                  CheckedStatusEnum.OPEN ? (
                                    <Icon
                                      data-task-status-caret=""
                                      className={classCss`
                                  margin-left: 3px;
                                `}
                                      size={isSmall ? 8 : 10}
                                      color={
                                        disabled ? "darkBlue30" : "darkBlue70"
                                      }
                                      name={
                                        isOpened &&
                                        openedCheckboxElement.element ===
                                          "STATUS_DROPDOWN"
                                          ? "up"
                                          : "down"
                                      }
                                    />
                                  ) : null}
                                  {renderTrailingWithCaret?.(
                                    taskCheckboxSlots,
                                  )}
                                </span>
                              ) : null}
                            </>
                          ),
                          Label: () => (
                            <WithTooltip
                              visible={tooltipLength < label.length}
                              label={label}
                              disabled={disabled}
                            >
                              <LabelText strikeThrough={isFinal}>
                                {label}
                              </LabelText>
                            </WithTooltip>
                          ),
                          FormattedDate: (
                            customProps: Record<string, unknown>,
                          ) => (
                            <FormattedDate
                              disabled={disabled}
                              onClick={event => {
                                if (!disabled) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setOpenedCheckboxElement({
                                    name,
                                    element: "DATEPICKER",
                                  });
                                }
                              }}
                              {...customProps}
                            >
                              {fieldValue.checkedDate
                                ? format(
                                    deserializeDateValue(fieldValue.checkedDate),
                                    DATE_FORMAT.SHORT_DAY_IN_MONTH,
                                  )
                                : null}
                            </FormattedDate>
                          ),
                        };

                        return children(taskCheckboxSlots);
                      })()}
                    </Label>
                  </CheckboxWrapper>
                </div>
              )}
            </Reference>
            {isOpened && openedCheckboxElement.element === "STATUS_DROPDOWN" ? (
              <ClickOutsideWrapper data-dashboard-card-stop="">
                <OutsideClickHandler
                  onOutsideClick={() => {
                    form.setFieldTouched(name, true, true);
                    setCheckboxClosed();
                  }}
                >
                  <Popper placement="bottom-start">
                    {({ref, style, placement}) => (
                      <StyledDropdownSelectList
                        ref={ref}
                        style={style}
                        data-placement={placement}
                      >
                        <StyledDropdownSelectOption
                          active={
                            currentCheckedStatus === CheckedStatusEnum.OPEN
                          }
                          onClick={() => {
                            if (
                              currentCheckedStatus === CheckedStatusEnum.OPEN
                            ) {
                              setCheckboxClosed();
                              return;
                            }

                            form.setFieldTouched(name, true, true);
                            form.setFieldValue(name, emptyValue, true);
                            setCheckboxClosed();
                          }}
                        >
                          <Icon
                            size={14}
                            color="darkBlue70"
                            name="circle-empty"
                          />
                          Open
                        </StyledDropdownSelectOption>
                        <StyledDropdownSelectOption
                          active={
                            currentCheckedStatus ===
                            CheckedStatusEnum.IN_PROGRESS
                          }
                          onClick={() =>
                            handleStatusChange(CheckedStatusEnum.IN_PROGRESS)
                          }
                        >
                          <Icon
                            size={18}
                            color="primary"
                            name="angle-double-right"
                          />
                          In Progress
                        </StyledDropdownSelectOption>
                        <StyledDropdownSelectOption
                          active={
                            currentCheckedStatus === CheckedStatusEnum.COMPLETED
                          }
                          onClick={() => {
                            const setStatusCompleted = () =>
                              handleStatusChange(CheckedStatusEnum.COMPLETED);

                            onComplete
                              ? onComplete({
                                  setCheckboxClosed,
                                  setStatusCompleted,
                                })
                              : setStatusCompleted();
                          }}
                        >
                          <Icon size={14} color="mint100" name="check" />
                          Completed
                        </StyledDropdownSelectOption>
                        {renderNotApplicableStatusOption({
                          StyledDropdownSelectOption,
                          onClick: () =>
                            handleStatusChange(
                              CheckedStatusEnum.NOT_APPLICABLE,
                            ),
                          active:
                            currentCheckedStatus ===
                            CheckedStatusEnum.NOT_APPLICABLE,
                          icon: (
                            <Icon
                              size={18}
                              color="mint100"
                              name="block"
                              className={classCss`
                                top: 2px;
                              `}
                            />
                          ),
                        })}
                      </StyledDropdownSelectList>
                    )}
                  </Popper>
                </OutsideClickHandler>
              </ClickOutsideWrapper>
            ) : isOpened && openedCheckboxElement.element === "DATEPICKER" ? (
              <ClickOutsideWrapper data-dashboard-card-stop="">
                <OutsideClickHandler
                  onOutsideClick={() => {
                    form.setFieldTouched(name, true, true);
                    setCheckboxClosed();
                  }}
                >
                  <Popper placement="bottom">
                    {({ref, style}) => (
                      <DatePicker
                        ref={ref}
                        style={style}
                        date={deserializeDateValue(fieldValue.checkedDate)}
                        range={{
                          before: [getDateRangeBefore(), "day"],
                          after: [getDateRangeAfter(), "day"],
                        }}
                        onChange={handleDateChange}
                      />
                    )}
                  </Popper>
                </OutsideClickHandler>
              </ClickOutsideWrapper>
            ) : null}
          </Manager>
        );
      }}
    </Field>
  );
};

export const TaskCheckboxSmall = (props: TaskCheckboxFieldProps) => (
  <TaskCheckboxField {...taskCheckboxSmallOverrides} {...props} />
);

export const TaskCheckboxFadedSmall = (props: TaskCheckboxFieldProps) => (
  <TaskCheckboxField {...taskCheckboxFadedSmallOverrides} {...props} />
);
